'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import {
  PixumEnvUtils,
  getJentisUserId,
  getJentisSessionId,
  isPageProductDetail,
  trackPageView,
  type PixumEnvType,
} from 'utils';

export interface PixumEnvExtensionApplierProps {
  userExtension: Partial<PixumEnvType['USER']>;
}

/**
 * Applies PIXUM_ENV updates on every render via useEffect.
 *
 * The PIXUM_ENV effect has no dependency array intentionally: it must re-execute
 * after router.refresh() on login/logout so the env object always reflects the
 * latest auth state. next/Script with a global ID would silently skip
 * re-execution when auth state changes.
 *
 * Page view tracking runs on mount and on every pathname change (client-side
 * navigation). It uses onceExtended to guarantee USER.id is populated before the
 * event is pushed — this is safe because the USER update effect (defined first)
 * always calls notifyExtended() before this effect fires on mount.
 *
 * Effect order matters: React runs effects in definition order within a component.
 * The USER update effect must be defined FIRST so USER.id is populated before
 * the pageview effect reads it on initial mount — otherwise the tracked user ID
 * would always be empty for logged-in sessions.
 */
export function PixumEnvExtensionApplier({ userExtension }: PixumEnvExtensionApplierProps) {
  const pathname = usePathname();

  // Update PIXUM_ENV on every render so login/logout state is always current.
  // Defined first so USER.id is set before the pageview effect reads it on mount.
  useEffect(() => {
    const jentisUserId = getJentisUserId();
    const jentisSessionId = getJentisSessionId();

    PixumEnvUtils.update('USER', {
      ...userExtension,
      ...(jentisUserId && { jentisUserId }),
      ...(jentisSessionId && { jentisSessionId }),
    });

    if (userExtension.pref) {
      PixumEnvUtils.update('TRACKING', { PREF: userExtension.pref });
    }

    PixumEnvUtils.notifyExtended();
  });

  // Track page view on mount and on client-side navigation (pathname change).
  // onceExtended ensures USER.id is populated before the event is pushed.
  useEffect(() => PixumEnvUtils.onceExtended(() => {
      if (!isPageProductDetail()) {
        // Jentis's "Prepare Pageview Data" code block determines JENTIS.page.type from URL
        // patterns configured in the Jentis backend. New cart URLs are not yet recognised
        // there, so they fall back to 'content'. We override with PAGE_TYPE when it's
        // explicitly set (e.g. 'checkout' for cart pages via PixumEnvScript).
        const pageType = PixumEnvUtils.get('PAGE_TYPE');
        const jentisPage = PixumEnvUtils.get('JENTIS')?.page;

        if (pageType && jentisPage) {
          PixumEnvUtils.update('JENTIS', { page: { ...jentisPage, type: pageType } });
        }

        trackPageView();
      }
    }), [pathname]);

  return null;
}
